/**
 * Cross-App Progress Aggregator
 *
 * Reads data from all 4 app namespaces via storageService and produces
 * unified statistics for the Progress Dashboard.
 * All functions are synchronous (localStorage reads).
 */

import storageService from '../storage/storageService';
import { DOMAINS } from '../constants/domains';

/**
 * Get aggregate stats across all apps.
 */
export function getAggregateStats() {
  const cards = storageService.get('flashcards', 'all', []);
  const flashSessions = storageService.get('flashcards', 'session-history', []);
  const drillSessions = storageService.get('cmddrill', 'sessions', []);
  const exams = storageService.get('examsim', 'exams', []);

  const totalCardsStudied = cards.reduce((sum, c) => sum + c.stats.times_seen, 0);
  const examsCompleted = exams.length;

  const drillAccuracy = drillSessions.length > 0
    ? drillSessions.reduce((sum, s) => sum + (s.accuracy || 0), 0) / drillSessions.length
    : 0;

  const overallReadiness = computeOverallReadiness(cards, exams);

  return {
    totalCardsStudied,
    totalFlashcardSessions: flashSessions.length,
    examsCompleted,
    drillSessions: drillSessions.length,
    drillAccuracy,
    overallReadiness,
    totalCards: cards.length,
  };
}

/**
 * Get domain accuracy combining flashcard and exam data.
 * Returns { domainId: { flashcardAcc, examAcc, combined, weight, name, color } }
 */
export function getDomainAccuracyAcrossApps() {
  const cards = storageService.get('flashcards', 'all', []);
  const exams = storageService.get('examsim', 'exams', []);

  const result = {};

  for (const domain of DOMAINS) {
    // Flashcard accuracy for this domain
    const domainCards = cards.filter(c => c.domain === domain.id);
    const fcSeen = domainCards.reduce((s, c) => s + c.stats.times_seen, 0);
    const fcCorrect = domainCards.reduce((s, c) => s + c.stats.times_correct, 0);
    const flashcardAcc = fcSeen > 0 ? fcCorrect / fcSeen : 0;

    // Exam accuracy for this domain (across all exams)
    let examCorrect = 0;
    let examTotal = 0;
    for (const exam of exams) {
      const dp = exam.score?.per_domain?.[domain.id];
      if (dp) {
        examCorrect += dp.correct;
        examTotal += dp.total;
      }
    }
    const examAcc = examTotal > 0 ? examCorrect / examTotal : 0;

    // Combined: weight exam accuracy higher (60/40) since it's the ground truth
    const hasFlashcard = fcSeen > 0;
    const hasExam = examTotal > 0;
    let combined;
    if (hasFlashcard && hasExam) {
      combined = flashcardAcc * 0.4 + examAcc * 0.6;
    } else if (hasFlashcard) {
      combined = flashcardAcc;
    } else if (hasExam) {
      combined = examAcc;
    } else {
      combined = 0;
    }

    result[domain.id] = {
      flashcardAcc: Math.round(flashcardAcc * 100),
      examAcc: Math.round(examAcc * 100),
      combined: Math.round(combined * 100),
      weight: domain.weight,
      name: domain.name,
      color: domain.color,
      shortName: domain.shortName,
    };
  }

  return result;
}

/**
 * Get exam score trend over time.
 */
export function getScoreTrend() {
  const exams = storageService.get('examsim', 'exams', []);

  return exams
    .map(e => ({
      date: e.started_at?.split('T')[0] || e.completed_at?.split('T')[0],
      percentage: e.score?.percentage || 0,
      scaled: e.score?.estimated_scaled || 0,
      passed: e.score?.passed || false,
      type: e.type,
    }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * Get study activity per day for the last N days.
 */
export function getStudyActivity(days = 30) {
  const flashSessions = storageService.get('flashcards', 'session-history', []);
  const drillSessions = storageService.get('cmddrill', 'sessions', []);
  const exams = storageService.get('examsim', 'exams', []);

  const activityMap = {};

  // Count sessions per date
  for (const s of flashSessions) {
    const d = s.date || s.started_at?.split('T')[0];
    if (!d) continue;
    if (!activityMap[d]) activityMap[d] = { flashcard: 0, drill: 0, exam: 0 };
    activityMap[d].flashcard++;
  }

  for (const s of drillSessions) {
    const d = s.date?.split('T')[0] || s.date;
    if (!d) continue;
    if (!activityMap[d]) activityMap[d] = { flashcard: 0, drill: 0, exam: 0 };
    activityMap[d].drill++;
  }

  for (const e of exams) {
    const d = e.started_at?.split('T')[0];
    if (!d) continue;
    if (!activityMap[d]) activityMap[d] = { flashcard: 0, drill: 0, exam: 0 };
    activityMap[d].exam++;
  }

  // Build array for last N days
  const result = [];
  const today = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    const dateStr = date.toISOString().split('T')[0];
    const activity = activityMap[dateStr] || { flashcard: 0, drill: 0, exam: 0 };
    result.push({
      date: dateStr,
      shortDate: `${date.getMonth() + 1}/${date.getDate()}`,
      ...activity,
      total: activity.flashcard + activity.drill + activity.exam,
    });
  }

  return result;
}

/**
 * Get per-domain readiness scores.
 */
export function getDomainReadiness() {
  const domainAcc = getDomainAccuracyAcrossApps();
  const cards = storageService.get('flashcards', 'all', []);

  return DOMAINS.map(domain => {
    const acc = domainAcc[domain.id];
    const domainCards = cards.filter(c => c.domain === domain.id);
    const coverage = domainCards.length > 0
      ? Math.min(1, domainCards.filter(c => c.stats.times_seen > 0).length / domainCards.length)
      : 0;

    // Readiness = accuracy × coverage (can't be ready if you haven't seen the cards)
    const readiness = Math.round(acc.combined * coverage);

    return {
      domainId: domain.id,
      name: domain.name,
      shortName: domain.shortName,
      color: domain.color,
      weight: domain.weight,
      readiness,
      accuracy: acc.combined,
      coverage: Math.round(coverage * 100),
    };
  });
}

/**
 * Compute overall readiness weighted by domain exam percentages.
 */
function computeOverallReadiness(cards, exams) {
  const domainAcc = getDomainAccuracyAcrossApps();
  let weighted = 0;
  let hasData = false;

  for (const domain of DOMAINS) {
    const acc = domainAcc[domain.id];
    if (acc.combined > 0) hasData = true;
    weighted += (acc.combined / 100) * domain.weight;
  }

  return hasData ? Math.round(weighted * 100) : 0;
}
